/**
 * Reach HUD tuning (plan R15): `src/hud/reach/reach-hud.json`, Zod-validated here. Layout-free numbers only: marker
 * margins, the nearby-label range, cap and declutter (spec 2026-10-09 H2), the scan ring's hold time, and the banner and toast timings. The travel
 * thresholds the HUD displays (charge time, escape seconds, relay alignment and speed) come from R6's
 * reach-travel.json, so the HUD never disagrees with the rules.
 */
import { z } from 'zod'
import hudJson from './reach-hud.json'

const positive = z.number().positive()
const nonNeg = z.number().nonnegative()
const count = z.number().int().positive().max(64)
const cap = z.number().int().nonnegative().max(64)

/** The HUD labels setting (spec 2026-10-09 §5.6 and H2): how many nearby labels keep their text. */
export const HUD_LABEL_MODES = ['full', 'reduced', 'minimal'] as const
export type HudLabelMode = (typeof HUD_LABEL_MODES)[number]

export const reachHudConfigSchema = z.object({
  comment: z.string().optional(),
  nav: z.object({
    /** Inset of the off-screen nav arrow from the viewport edge (CSS px). */
    edgeMarginPx: nonNeg,
  }),
  labels: z
    .object({
      /** Structures within this distance of the player get a label (m). */
      rangeM: positive,
      /** Label pool size. */
      max: count,
      /** Closer than this a label is hidden: you are looking at the thing (m). */
      minDistanceM: nonNeg,
      /** The default HUD labels setting until Settings › Interface carries it. */
      mode: z.enum(HUD_LABEL_MODES),
      /** Full labels per setting; lower-priority labels beyond the cap keep the icon only (H2). */
      caps: z.object({ full: cap, reduced: cap, minimal: cap }),
      /** Label priority by structure kind: lower first, then nearer first. */
      kindRank: z.object({ station: nonNeg, relay: nonNeg, poi: nonNeg, laneRing: nonNeg }),
      /** The greedy overlap test's screen rectangles and the distance fade (CSS px, m). */
      declutter: z.object({
        /** Average glyph advance of label text at --fs-meta, letter spacing included. */
        charPx: positive,
        /** Characters reserved for the distance ("12.3 km"). */
        distChars: nonNeg,
        /** Plate padding plus the gap between name and distance, summed. */
        platePadPx: nonNeg,
        plateHeightPx: positive,
        /** The plate's left edge, right of the icon. */
        offsetXPx: nonNeg,
        /** The nav and route markers: half height of the diamond, and the depth of the text under it. */
        markerHalfHeightPx: nonNeg,
        markerTextPx: nonNeg,
        /** While a target is locked, labels inside this radius of the screen centre are suppressed. */
        reticleRadiusPx: nonNeg,
        /** Labels fade from full opacity at this distance to fadeMinOpacity at rangeM (m). */
        fadeStartM: nonNeg,
        fadeMinOpacity: z.number().min(0).max(1),
      }),
    })
    .refine((l) => l.caps.full <= l.max && l.caps.reduced <= l.max && l.caps.minimal <= l.max, { message: 'labels.caps must not exceed labels.max' }),
  /** Edge-pinned nav markers (W1 polish): the safe rectangle that keeps them off the screen edge and the HUD panels. */
  edgeMarkers: z.object({
    /** Insets of the safe rectangle from the viewport (CSS px): bottom clears the status, weapons and cruise panels. */
    insetPx: z.object({ left: nonNeg, right: nonNeg, top: nonNeg, bottom: nonNeg }),
    /** Minimum vertical gap between markers pinned to the same side (CSS px). */
    spacingPx: positive,
    /** The rectangle around an edge arrow and its distance readout that labels avoid (CSS px from the arrow). */
    arrowRectPx: z.object({ left: nonNeg, right: nonNeg, up: nonNeg, down: nonNeg }),
  }),
  farMarkers: z.object({
    /** Far nav marker pool size: the nearest lane rings and relays of the system get one (D-249). */
    max: count,
    /** Closer than this a far marker hides and the nearby label takes over (m; match labels.rangeM). */
    minDistanceM: nonNeg,
    /** Opacity of the far-marker layer, kept dimmer than the nav target's marker. */
    opacity: z.number().min(0).max(1),
    /** In cruise or lane travel at or above this throttle, a busy sky hides the far markers. */
    hideCruiseThrottle: z.number().min(0).max(1),
    /** More far markers than this counts as busy. */
    busyCount: z.number().int().nonnegative(),
  }),
  scan: z.object({
    /** The deep-scan hold time the ring fills over (s); mirrors reach-explore.json `scanner.holdS`. */
    holdS: positive,
    /** How long the ping ring expands after a scan (s). */
    pingS: positive,
  }),
  /**
   * text (CL-7, D-1158 Q3): the banner sub after the region: owned names the layout owner ("Authority space"),
   * unowned a system no faction owns, and unclaimed is appended when the Charter says no one holds the claim.
   */
  banner: z.object({
    seconds: positive,
    fadeInS: nonNeg,
    fadeOutS: nonNeg,
    text: z.object({ owned: z.string().min(1), unowned: z.string().min(1), unclaimed: z.string().min(1) }),
  }),
  toasts: z.object({
    /** Visible toast slots. */
    max: count,
    /** Pending toasts kept while every slot is busy (the oldest pending one is dropped beyond it). */
    queue: count,
    seconds: positive,
    fadeS: nonNeg,
    /** A chart-value toast for the same system within this long adds to the one already showing (s). */
    coalesceS: nonNeg,
  }),
  cruise: z.object({
    /** The hold-to-cruise prompt shows while the drive is ready and the nav target is farther than this (m). */
    promptMinDistanceM: nonNeg,
  }),
  device: z.object({
    /** A stick beyond this (or any pad button) makes the pad the active device for prompt glyphs. */
    padAxisThreshold: z.number().min(0).max(1),
  }),
})

export type ReachHudConfig = z.infer<typeof reachHudConfigSchema>

/** Parses and validates reach-hud.json (throws a ZodError naming the bad field). */
export function loadReachHudConfig(raw: unknown = hudJson): ReachHudConfig {
  return reachHudConfigSchema.parse(raw)
}
